import { useState, useEffect } from "react";
import { AdminLayout } from "@/components/layout/AdminLayout";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Button } from "@/components/ui/button";
import { Badge } from "@/components/ui/badge";
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from "@/components/ui/dialog";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from "@/components/ui/table";
import { trpc } from "@/lib/trpc";
import {
  Search,
  Headset,
  AlertCircle,
  Clock,
  CheckCircle2,
  Filter,
  Send,
  Plus,
  User,
  Building2,
  FileText,
  MessageSquare,
  WifiOff,
  RefreshCw,
  BarChart3,
  TrendingDown,
  ShieldAlert,
} from "lucide-react";
import { useAuth } from "@/contexts/AuthContext";
import { canWrite } from "@/lib/rbac";
import { ListPageSkeleton } from "@/components/PageSkeleton";
import { Skeleton } from "@/components/ui/skeleton";
import { formatDate } from "@/lib/demo-data";
import { useSearchParams } from "react-router-dom";
import { toast } from "sonner";

const PRIORITY_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  LOW: { label: "Basse", variant: "outline" },
  MEDIUM: { label: "Moyenne", variant: "secondary" },
  HIGH: { label: "Haute", variant: "default" },
  URGENT: { label: "Urgent", variant: "destructive" },
};

const STATUS_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline" }> = {
  NEW: { label: "Nouveau", variant: "outline" },
  IN_PROGRESS: { label: "En cours", variant: "default" },
  WAITING_CLIENT: { label: "Attente client", variant: "secondary" },
  ESCALATED: { label: "Escaladé", variant: "destructive" },
  RESOLVED: { label: "Résolu", variant: "default" },
  CLOSED: { label: "Fermé", variant: "secondary" },
};

const TYPE_LABELS: Record<string, string> = {
  ACCOUNT_ACCESS: "Accès compte",
  PROFILE_CONFIGURATION: "Config. profil",
  MATCHING_ISSUE: "Pb matching",
  TENDER_VISIBILITY: "Visibilité AO",
  DOCUMENT_ISSUE: "Pb documents",
  PROJECT_BLOCKER: "Blocage projet",
  BILLING_ISSUE: "Pb facturation",
  BUG_REPORT: "Bug",
  GENERAL_SUPPORT: "Support général",
};

const SOURCE_CONFIG: Record<string, { label: string; variant: "default" | "secondary" | "destructive" | "outline"; className?: string }> = {
  CONSTRIGO: { label: "Constrigo", variant: "outline" },
  TRAVAUXBTP: { label: "TravauxBTP", variant: "secondary", className: "bg-orange-100 text-orange-700 border-orange-200" },
};

type TicketFilter = "all" | "open" | "unassigned" | "my";
type SupportView = "tickets" | "analytics";
type AnalyticsPeriod = "7d" | "30d" | "90d";

export default function Support() {
  const { user } = useAuth();
  const isWriter = canWrite(user?.staffRole || null);
  const [searchParams] = useSearchParams();
  const [page, setPage] = useState(1);
  const [ticketFilter, setTicketFilter] = useState<TicketFilter>("open");
  const [supportView, setSupportView] = useState<SupportView>("tickets");
  const [analyticsPeriod, setAnalyticsPeriod] = useState<AnalyticsPeriod>("30d");
  const [priorityFilter, setPriorityFilter] = useState<string>("");
  const [typeFilter, setTypeFilter] = useState<string>("");
  const [sourceFilter, setSourceFilter] = useState<string>("");
  const [selectedTicketId, setSelectedTicketId] = useState<string | null>(null);
  const [noteText, setNoteText] = useState("");
  const [showCreateDialog, setShowCreateDialog] = useState(false);

  // Create form state
  const [createForm, setCreateForm] = useState({
    companyId: "",
    subject: "",
    description: "",
    type: "GENERAL_SUPPORT" as string,
    priority: "MEDIUM" as string,
  });

  // Read filters from URL
  useEffect(() => {
    const f = searchParams.get("filter");
    const p = searchParams.get("priority");
    if (f === "open") setTicketFilter("open");
    else if (f === "unassigned") setTicketFilter("unassigned");
    else if (f === "my") setTicketFilter("my");
    else if (f === "all") setTicketFilter("all");
    if (p) setPriorityFilter(p);
  }, [searchParams]);

  const { data: stats, isLoading: statsLoading, isError: statsError, refetch: refetchStats } = trpc.admin.support.stats.useQuery();

  const { data, isLoading, isError: listError, refetch } = trpc.admin.support.list.useQuery({
    page,
    limit: 20,
    open: ticketFilter === "open" || ticketFilter === "my" ? true : undefined,
    unassigned: ticketFilter === "unassigned" ? true : undefined,
    priority: priorityFilter ? (priorityFilter as any) : undefined,
    type: typeFilter ? (typeFilter as any) : undefined,
    source: sourceFilter ? (sourceFilter as any) : undefined,
  });

  const { data: ticketDetail, isLoading: ticketLoading, isError: ticketError, refetch: refetchDetail } = trpc.admin.support.get.useQuery(
    { id: selectedTicketId! },
    { enabled: !!selectedTicketId }
  );

  // Companies for create form
  const { data: companiesData, isError: companiesError } = trpc.admin.companies.list.useQuery(
    { page: 1, limit: 100 },
    { enabled: showCreateDialog }
  );

  const utils = trpc.useUtils();

  const createMutation = trpc.admin.support.create.useMutation({
    onSuccess: () => {
      toast.success("Ticket créé");
      setShowCreateDialog(false);
      setCreateForm({ companyId: "", subject: "", description: "", type: "GENERAL_SUPPORT", priority: "MEDIUM" });
      refetch();
      utils.admin.support.stats.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const assignMutation = trpc.admin.support.assign.useMutation({
    onSuccess: () => {
      toast.success("Ticket assigné");
      refetchDetail();
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const resolveMutation = trpc.admin.support.resolve.useMutation({
    onSuccess: () => {
      toast.success("Ticket résolu");
      refetchDetail();
      refetch();
      utils.admin.support.stats.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const closeMutation = trpc.admin.support.close.useMutation({
    onSuccess: () => {
      toast.success("Ticket fermé");
      refetchDetail();
      refetch();
      utils.admin.support.stats.invalidate();
    },
    onError: (err) => toast.error(err.message),
  });

  const updateMutation = trpc.admin.support.update.useMutation({
    onSuccess: () => {
      toast.success("Ticket mis à jour");
      refetchDetail();
      refetch();
    },
    onError: (err) => toast.error(err.message),
  });

  const addNoteMutation = trpc.admin.support.addNote.useMutation({
    onSuccess: () => {
      toast.success("Note ajoutée");
      setNoteText("");
      refetchDetail();
    },
    onError: (err) => toast.error(err.message),
  });

  const { data: analyticsData, isLoading: analyticsLoading } =
    trpc.admin.support.getAnalytics.useQuery(
      { period: analyticsPeriod },
      { enabled: supportView === "analytics" }
    );

  const handleCreate = () => {
    if (!createForm.companyId || !createForm.subject.trim()) return;
    createMutation.mutate({
      companyId: createForm.companyId,
      subject: createForm.subject.trim(),
      description: createForm.description.trim() || undefined,
      type: createForm.type as any,
      priority: createForm.priority as any,
    });
  };

  const handleAssignToMe = () => {
    if (!selectedTicketId || !user?.id) return;
    assignMutation.mutate({ id: selectedTicketId, assigneeId: user.id });
  };

  const handleAddNote = () => {
    if (!noteText.trim() || !selectedTicketId) return;
    addNoteMutation.mutate({ ticketId: selectedTicketId, message: noteText.trim() });
  };

  return (
    <AdminLayout>
      <div className="space-y-6">
        <div className="flex justify-between items-center">
          <div>
            <h1 className="text-3xl font-semibold">Support</h1>
            <p className="text-muted-foreground">Gestion des tickets de support client</p>
          </div>
          <div className="flex items-center gap-2">
            <Button
              variant={supportView === "analytics" ? "default" : "outline"}
              size="sm"
              onClick={() => setSupportView(supportView === "analytics" ? "tickets" : "analytics")}
            >
              <BarChart3 className="h-4 w-4 mr-1.5" />
              Analytics
            </Button>
            {isWriter && supportView === "tickets" && (
              <Button onClick={() => setShowCreateDialog(true)}>
                <Plus className="h-4 w-4 mr-2" />
                Nouveau ticket
              </Button>
            )}
          </div>
        </div>

        {/* ── Analytics view ── */}
        {supportView === "analytics" && (
          <div className="space-y-6">
            {/* Period selector */}
            <div className="flex items-center gap-2">
              <span className="text-sm text-muted-foreground font-medium">Période :</span>
              {(["7d", "30d", "90d"] as AnalyticsPeriod[]).map((p) => (
                <button
                  key={p}
                  type="button"
                  onClick={() => setAnalyticsPeriod(p)}
                  className={`px-3 py-1 rounded-md text-sm font-medium transition-colors ${
                    analyticsPeriod === p
                      ? "bg-primary text-primary-foreground"
                      : "bg-muted text-muted-foreground hover:bg-muted/80"
                  }`}
                >
                  {p === "7d" ? "7 jours" : p === "30d" ? "30 jours" : "90 jours"}
                </button>
              ))}
            </div>

            {analyticsLoading ? (
              <div className="text-center py-12 text-muted-foreground text-sm">Calcul des analytics...</div>
            ) : analyticsData ? (
              <>
                {/* Volume KPIs */}
                <div className="grid grid-cols-2 gap-4 md:grid-cols-4">
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Total tickets</CardTitle>
                      <FileText className="h-4 w-4 text-muted-foreground" />
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold">{analyticsData.volume.total}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Ouverts</CardTitle>
                      <Clock className="h-4 w-4 text-orange-500" />
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold text-orange-600">{analyticsData.volume.open}</div></CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">Résolus</CardTitle>
                      <CheckCircle2 className="h-4 w-4 text-green-500" />
                    </CardHeader>
                    <CardContent><div className="text-2xl font-bold text-green-600">{analyticsData.volume.resolved}</div></CardContent>
                  </Card>
                  <Card className={analyticsData.overdueTickets.length > 0 ? "border-red-200" : ""}>
                    <CardHeader className="flex flex-row items-center justify-between pb-2">
                      <CardTitle className="text-sm font-medium text-muted-foreground">En retard SLA</CardTitle>
                      <ShieldAlert className={`h-4 w-4 ${analyticsData.overdueTickets.length > 0 ? "text-red-500" : "text-muted-foreground"}`} />
                    </CardHeader>
                    <CardContent>
                      <div className={`text-2xl font-bold ${analyticsData.overdueTickets.length > 0 ? "text-red-600" : ""}`}>
                        {analyticsData.overdueTickets.length}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Time metrics + SLA */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-3">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Temps moyen 1ère réponse</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {analyticsData.avgFirstResponseTimeHours != null
                          ? `${analyticsData.avgFirstResponseTimeHours}h`
                          : "—"}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Temps moyen de résolution</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className="text-2xl font-bold">
                        {analyticsData.avgResolutionTimeHours != null
                          ? `${analyticsData.avgResolutionTimeHours}h`
                          : "—"}
                      </div>
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Conformité SLA</CardTitle>
                    </CardHeader>
                    <CardContent>
                      <div className={`text-2xl font-bold ${
                        analyticsData.slaComplianceRate != null && analyticsData.slaComplianceRate >= 90
                          ? "text-green-600"
                          : analyticsData.slaComplianceRate != null && analyticsData.slaComplianceRate >= 70
                          ? "text-orange-600"
                          : "text-red-600"
                      }`}>
                        {analyticsData.slaComplianceRate != null ? `${analyticsData.slaComplianceRate}%` : "—"}
                      </div>
                    </CardContent>
                  </Card>
                </div>

                {/* Distributions */}
                <div className="grid grid-cols-1 gap-4 md:grid-cols-2">
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Par type</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {Object.entries(analyticsData.typeDistribution)
                        .sort((a, b) => b[1] - a[1])
                        .map(([type, count]) => {
                          // Fallback `1` pour éviter Math.max([]) = -Infinity sur distribution vide.
                          const max = Math.max(...Object.values(analyticsData.typeDistribution), 1);
                          const pct = max > 0 ? Math.round((count / max) * 100) : 0;
                          return (
                            <div key={type} className="flex items-center gap-3">
                              <span className="text-xs text-muted-foreground w-36 truncate">{TYPE_LABELS[type] ?? type}</span>
                              <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                                <div className="h-full bg-primary rounded-full" style={{ width: `${pct}%` }} />
                              </div>
                              <span className="text-xs font-medium w-6 text-right">{count}</span>
                            </div>
                          );
                        })}
                    </CardContent>
                  </Card>
                  <Card>
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm">Par priorité</CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {(["URGENT", "HIGH", "MEDIUM", "LOW"] as const).map((priority) => {
                        const count = analyticsData.priorityDistribution[priority] ?? 0;
                        const total = analyticsData.volume.total;
                        const pct = total > 0 ? Math.round((count / total) * 100) : 0;
                        const color = priority === "URGENT" ? "bg-red-500" : priority === "HIGH" ? "bg-orange-500" : priority === "MEDIUM" ? "bg-yellow-500" : "bg-green-500";
                        const labels: Record<string, string> = { URGENT: "Urgent", HIGH: "Haute", MEDIUM: "Moyenne", LOW: "Basse" };
                        return (
                          <div key={priority} className="flex items-center gap-3">
                            <span className="text-xs text-muted-foreground w-20">{labels[priority]}</span>
                            <div className="flex-1 h-2 bg-muted rounded-full overflow-hidden">
                              <div className={`h-full rounded-full ${color}`} style={{ width: `${pct}%` }} />
                            </div>
                            <span className="text-xs font-medium w-6 text-right">{count}</span>
                          </div>
                        );
                      })}
                    </CardContent>
                  </Card>
                </div>

                {/* Overdue tickets alert */}
                {analyticsData.overdueTickets.length > 0 && (
                  <Card className="border-red-200 bg-red-50/50">
                    <CardHeader className="pb-2">
                      <CardTitle className="text-sm text-red-800 flex items-center gap-2">
                        <AlertCircle className="h-4 w-4" />
                        Tickets en retard SLA ({analyticsData.overdueTickets.length})
                      </CardTitle>
                    </CardHeader>
                    <CardContent className="space-y-2">
                      {analyticsData.overdueTickets.slice(0, 10).map((t) => (
                        <div key={t.id} className="flex items-center justify-between text-sm">
                          <span className="truncate flex-1 text-muted-foreground">{t.subject}</span>
                          <div className="flex items-center gap-2 ml-2">
                            <Badge variant="destructive" className="text-xs">{t.priority}</Badge>
                            <span className="text-xs text-red-700 font-medium whitespace-nowrap">{t.ageHours}h</span>
                          </div>
                        </div>
                      ))}
                    </CardContent>
                  </Card>
                )}
              </>
            ) : null}
          </div>
        )}

        {supportView === "tickets" && (<>
        {/* Stats cards */}
        {statsLoading ? (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            {Array.from({ length: 5 }).map((_, i) => (
              <Card key={i}>
                <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                  <Skeleton className="h-4 w-24" />
                  <Skeleton className="h-4 w-4 rounded" />
                </CardHeader>
                <CardContent>
                  <Skeleton className="h-8 w-12" />
                </CardContent>
              </Card>
            ))}
          </div>
        ) : statsError ? (
          <div className="flex items-center gap-3 text-sm text-muted-foreground py-2">
            <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
            Impossible de charger les statistiques
            <Button variant="outline" size="sm" onClick={() => refetchStats()}>
              <RefreshCw className="h-3 w-3 mr-1" />
              Réessayer
            </Button>
          </div>
        ) : stats && (
          <div className="grid gap-3 md:grid-cols-2 lg:grid-cols-5">
            <Card className={`cursor-pointer transition-colors ${ticketFilter === "open" ? "border-primary" : "hover:bg-accent"}`}
              onClick={() => { setTicketFilter("open"); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Ouverts</CardTitle>
                <Headset className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-primary">{stats.totalOpen}</div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => { setPriorityFilter(priorityFilter === "URGENT" ? "" : "URGENT"); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Urgents</CardTitle>
                <AlertCircle className="h-4 w-4 text-destructive" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-destructive">{stats.totalUrgent}</div>
              </CardContent>
            </Card>

            <Card className={`cursor-pointer transition-colors ${ticketFilter === "unassigned" ? "border-warning" : "hover:bg-accent"}`}
              onClick={() => { setTicketFilter(ticketFilter === "unassigned" ? "open" : "unassigned"); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Non assignés</CardTitle>
                <User className="h-4 w-4 text-warning" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-warning">{stats.totalUnassigned}</div>
              </CardContent>
            </Card>

            <Card className="cursor-pointer hover:bg-accent transition-colors"
              onClick={() => { setTicketFilter("open"); setPriorityFilter(""); setPage(1); }}>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Nouveaux</CardTitle>
                <FileText className="h-4 w-4 text-primary" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold">{stats.totalNew}</div>
              </CardContent>
            </Card>

            <Card>
              <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-2">
                <CardTitle className="text-sm font-medium">Résolus (7j)</CardTitle>
                <CheckCircle2 className="h-4 w-4 text-success" />
              </CardHeader>
              <CardContent>
                <div className="text-2xl font-bold text-success">{stats.resolvedThisWeek}</div>
              </CardContent>
            </Card>
          </div>
        )}

        {/* Filters */}
        <Card>
          <CardContent className="pt-6">
            <div className="flex flex-wrap gap-4 items-center">
              <div className="flex items-center gap-2">
                <Filter className="h-4 w-4 text-muted-foreground" />
                <Select value={ticketFilter} onValueChange={(v) => { setTicketFilter(v as TicketFilter); setPage(1); }}>
                  <SelectTrigger className="w-40">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="open">Ouverts</SelectItem>
                    <SelectItem value="unassigned">Non assignés</SelectItem>
                    <SelectItem value="all">Tous</SelectItem>
                  </SelectContent>
                </Select>
              </div>
              <Select value={priorityFilter || "_all"} onValueChange={(v) => { setPriorityFilter(v === "_all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Toutes priorités" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Toutes priorités</SelectItem>
                  <SelectItem value="URGENT">Urgent</SelectItem>
                  <SelectItem value="HIGH">Haute</SelectItem>
                  <SelectItem value="MEDIUM">Moyenne</SelectItem>
                  <SelectItem value="LOW">Basse</SelectItem>
                </SelectContent>
              </Select>
              <Select value={typeFilter || "_all"} onValueChange={(v) => { setTypeFilter(v === "_all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-44">
                  <SelectValue placeholder="Tous types" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Tous types</SelectItem>
                  {Object.entries(TYPE_LABELS).map(([key, label]) => (
                    <SelectItem key={key} value={key}>{label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
              <Select value={sourceFilter || "_all"} onValueChange={(v) => { setSourceFilter(v === "_all" ? "" : v); setPage(1); }}>
                <SelectTrigger className="w-40">
                  <SelectValue placeholder="Toutes sources" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="_all">Toutes sources</SelectItem>
                  <SelectItem value="CONSTRIGO">Constrigo</SelectItem>
                  <SelectItem value="TRAVAUXBTP">TravauxBTP</SelectItem>
                </SelectContent>
              </Select>
            </div>
          </CardContent>
        </Card>

        {/* Tickets table */}
        <Card>
          <CardHeader className="pb-2">
            <div className="flex items-center justify-between">
              <CardTitle className="text-base">{data?.total || 0} ticket(s)</CardTitle>
            </div>
          </CardHeader>
          <CardContent>
            {isLoading ? (
              <ListPageSkeleton />
            ) : listError ? (
              <div className="flex flex-col items-center py-12 gap-2 text-muted-foreground">
                <WifiOff className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
                <p className="text-sm">Impossible de charger les tickets</p>
                <Button variant="outline" onClick={() => refetch()}>
                  <RefreshCw className="h-4 w-4 mr-2" />
                  Réessayer
                </Button>
              </div>
            ) : data && data.items.length > 0 ? (
              <Table>
                <TableHeader>
                  <TableRow>
                    <TableHead>Sujet</TableHead>
                    <TableHead>Source</TableHead>
                    <TableHead>Entreprise</TableHead>
                    <TableHead>Type</TableHead>
                    <TableHead>Priorité</TableHead>
                    <TableHead>Statut</TableHead>
                    <TableHead>Assigné</TableHead>
                    <TableHead>Notes</TableHead>
                    <TableHead>Créé le</TableHead>
                  </TableRow>
                </TableHeader>
                <TableBody>
                  {data.items.map((ticket: any) => (
                    <TableRow
                      key={ticket.id}
                      className="cursor-pointer hover:bg-accent"
                      onClick={() => setSelectedTicketId(ticket.id)}
                    >
                      <TableCell className="font-medium max-w-xs truncate">
                        {ticket.subject}
                      </TableCell>
                      <TableCell>
                        {(() => {
                          const src = SOURCE_CONFIG[ticket.source] || { label: ticket.source || "—", variant: "outline" as const };
                          return (
                            <Badge variant={src.variant} className={`text-xs ${src.className || ""}`}>
                              {src.label}
                            </Badge>
                          );
                        })()}
                      </TableCell>
                      <TableCell className="text-xs">
                        {ticket.Company?.name || "-"}
                      </TableCell>
                      <TableCell>
                        <Badge variant="outline" className="text-xs">
                          {TYPE_LABELS[ticket.type] || ticket.type}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={PRIORITY_CONFIG[ticket.priority]?.variant || "outline"} className="text-xs">
                          {PRIORITY_CONFIG[ticket.priority]?.label || ticket.priority}
                        </Badge>
                      </TableCell>
                      <TableCell>
                        <Badge variant={STATUS_CONFIG[ticket.status]?.variant || "outline"} className="text-xs">
                          {STATUS_CONFIG[ticket.status]?.label || ticket.status}
                        </Badge>
                      </TableCell>
                      <TableCell className="text-xs">
                        {ticket.Assignee
                          ? [ticket.Assignee.firstName, ticket.Assignee.lastName].filter(Boolean).join(" ") || ticket.Assignee.email
                          : <span className="text-warning">—</span>}
                      </TableCell>
                      <TableCell className="text-xs">
                        {ticket._count?.TicketNote || 0}
                      </TableCell>
                      <TableCell className="text-xs">{formatDate(ticket.createdAt)}</TableCell>
                    </TableRow>
                  ))}
                </TableBody>
              </Table>
            ) : (
              <div className="text-center py-8">
                <Headset className="h-12 w-12 text-muted-foreground/30 mx-auto mb-3" />
                <p className="text-muted-foreground">Aucun ticket trouvé</p>
              </div>
            )}

            {/* Pagination */}
            {data && data.total > data.limit && (
              <div className="flex justify-between items-center mt-4">
                <span className="text-sm text-muted-foreground">
                  Page {data.page} / {Math.ceil(data.total / data.limit)}
                </span>
                <div className="flex gap-2">
                  <Button size="sm" variant="outline" disabled={page <= 1} onClick={() => setPage(p => p - 1)}>Précédent</Button>
                  <Button size="sm" variant="outline" disabled={page >= Math.ceil(data.total / data.limit)} onClick={() => setPage(p => p + 1)}>Suivant</Button>
                </div>
              </div>
            )}
          </CardContent>
        </Card>
      </>)}
      </div>

      {/* Ticket Detail Dialog */}
      <Dialog open={!!selectedTicketId} onOpenChange={(open) => { if (!open) setSelectedTicketId(null); }}>
        <DialogContent className="max-w-3xl max-h-[90vh] overflow-y-auto">
          <DialogHeader>
            <DialogTitle className="flex items-center gap-3">
              <Headset className="h-5 w-5" />
              {ticketDetail?.subject || "Chargement..."}
            </DialogTitle>
            <DialogDescription>
              {ticketDetail?.Company?.name || ""} — Ticket #{ticketDetail?.id?.slice(-6) || ""}
            </DialogDescription>
          </DialogHeader>

          {ticketLoading ? (
            <div className="space-y-3 mt-4">
              <div className="grid grid-cols-2 md:grid-cols-4 gap-3">
                {Array.from({ length: 4 }).map((_, i) => (
                  <div key={i} className="p-3 border rounded-lg space-y-2">
                    <Skeleton className="h-3 w-12 mx-auto" />
                    <Skeleton className="h-5 w-16 mx-auto" />
                  </div>
                ))}
              </div>
              <Skeleton className="h-20 w-full" />
              <Skeleton className="h-4 w-3/4" />
              <Skeleton className="h-4 w-1/2" />
            </div>
          ) : ticketError ? (
            <div className="flex flex-col items-center py-12 gap-3 text-muted-foreground">
              <WifiOff className="h-8 w-8 text-muted-foreground/50" aria-hidden="true" />
              <p className="text-sm">Impossible de charger le ticket</p>
              <Button variant="outline" size="sm" onClick={() => refetchDetail()}>
                <RefreshCw className="h-4 w-4 mr-2" />
                Réessayer
              </Button>
            </div>
          ) : ticketDetail ? (
            <div className="space-y-4 mt-4">
              {/* Ticket metadata */}
              <div className="grid grid-cols-2 md:grid-cols-5 gap-3">
                <div className="text-center p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Source</p>
                  {(() => {
                    const src = SOURCE_CONFIG[ticketDetail.source] || { label: ticketDetail.source || "—", variant: "outline" as const };
                    return <Badge variant={src.variant} className={src.className || ""}>{src.label}</Badge>;
                  })()}
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Statut</p>
                  <Badge variant={STATUS_CONFIG[ticketDetail.status]?.variant || "outline"}>
                    {STATUS_CONFIG[ticketDetail.status]?.label || ticketDetail.status}
                  </Badge>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Priorité</p>
                  <Badge variant={PRIORITY_CONFIG[ticketDetail.priority]?.variant || "outline"}>
                    {PRIORITY_CONFIG[ticketDetail.priority]?.label || ticketDetail.priority}
                  </Badge>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Type</p>
                  <span className="text-sm font-medium">{TYPE_LABELS[ticketDetail.type] || ticketDetail.type}</span>
                </div>
                <div className="text-center p-3 border rounded-lg">
                  <p className="text-xs text-muted-foreground mb-1">Assigné à</p>
                  <span className="text-sm font-medium">
                    {ticketDetail.Assignee
                      ? [ticketDetail.Assignee.firstName, ticketDetail.Assignee.lastName].filter(Boolean).join(" ") || ticketDetail.Assignee.email
                      : <span className="text-warning">Non assigné</span>}
                  </span>
                </div>
              </div>

              {/* Links */}
              <div className="flex flex-wrap gap-3 text-sm">
                {ticketDetail.Company && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <Building2 className="h-3 w-3" />
                    {ticketDetail.Company.name}
                  </div>
                )}
                {ticketDetail.User && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <User className="h-3 w-3" />
                    {ticketDetail.User.email}
                  </div>
                )}
                {ticketDetail.ResponseProject && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    Projet: {ticketDetail.ResponseProject.Tender?.title || ticketDetail.ResponseProject.id}
                  </div>
                )}
                {ticketDetail.Tender && (
                  <div className="flex items-center gap-1 text-muted-foreground">
                    <FileText className="h-3 w-3" />
                    AO: {ticketDetail.Tender.title || ticketDetail.Tender.id}
                  </div>
                )}
              </div>

              {/* Description */}
              {ticketDetail.description && (
                <div className="border rounded-lg p-4">
                  <p className="text-sm font-medium mb-1">Description</p>
                  <p className="text-sm whitespace-pre-wrap text-muted-foreground">{ticketDetail.description}</p>
                </div>
              )}

              {/* Actions */}
              {isWriter && (
                <div className="flex flex-wrap gap-2 border-t pt-4">
                  {!ticketDetail.Assignee && (
                    <Button size="sm" variant="outline" onClick={handleAssignToMe}
                      disabled={assignMutation.isPending}>
                      <User className="h-4 w-4 mr-1" />
                      M'assigner
                    </Button>
                  )}
                  {ticketDetail.status !== "RESOLVED" && ticketDetail.status !== "CLOSED" && (
                    <>
                      <Select value={ticketDetail.priority} onValueChange={(v) => updateMutation.mutate({ id: ticketDetail.id, priority: v as any })}>
                        <SelectTrigger className="h-8 w-32 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="LOW">Basse</SelectItem>
                          <SelectItem value="MEDIUM">Moyenne</SelectItem>
                          <SelectItem value="HIGH">Haute</SelectItem>
                          <SelectItem value="URGENT">Urgent</SelectItem>
                        </SelectContent>
                      </Select>
                      <Select value={ticketDetail.status} onValueChange={(v) => updateMutation.mutate({ id: ticketDetail.id, status: v as any })}>
                        <SelectTrigger className="h-8 w-36 text-xs">
                          <SelectValue />
                        </SelectTrigger>
                        <SelectContent>
                          <SelectItem value="NEW">Nouveau</SelectItem>
                          <SelectItem value="IN_PROGRESS">En cours</SelectItem>
                          <SelectItem value="WAITING_CLIENT">Attente client</SelectItem>
                          <SelectItem value="ESCALATED">Escaladé</SelectItem>
                        </SelectContent>
                      </Select>
                      <Button size="sm" variant="default"
                        onClick={() => resolveMutation.mutate({ id: ticketDetail.id })}
                        disabled={resolveMutation.isPending}>
                        <CheckCircle2 className="h-4 w-4 mr-1" />
                        Résoudre
                      </Button>
                    </>
                  )}
                  {ticketDetail.status === "RESOLVED" && (
                    <Button size="sm" variant="outline"
                      onClick={() => closeMutation.mutate({ id: ticketDetail.id })}
                      disabled={closeMutation.isPending}>
                      Fermer
                    </Button>
                  )}
                </div>
              )}

              {/* Dates */}
              <div className="flex flex-wrap gap-4 text-xs text-muted-foreground">
                <span>Créé: {formatDate(ticketDetail.createdAt)}</span>
                {ticketDetail.resolvedAt && <span>Résolu: {formatDate(ticketDetail.resolvedAt)}</span>}
                {ticketDetail.closedAt && <span>Fermé: {formatDate(ticketDetail.closedAt)}</span>}
              </div>

              {/* Notes / Timeline */}
              <div className="border-t pt-4 space-y-3">
                <h4 className="font-semibold flex items-center gap-2">
                  <MessageSquare className="h-4 w-4" />
                  Notes ({ticketDetail.TicketNote?.length || 0})
                </h4>

                <div className="flex gap-2">
                  <textarea
                    className="flex-1 min-h-[60px] px-3 py-2 border rounded-md text-sm"
                    placeholder="Ajouter une note interne..."
                    value={noteText}
                    onChange={(e) => setNoteText(e.target.value)}
                  />
                  <Button size="sm" className="self-end" onClick={handleAddNote}
                    disabled={!noteText.trim() || addNoteMutation.isPending}>
                    <Send className="h-4 w-4" />
                  </Button>
                </div>

                {ticketDetail.TicketNote && ticketDetail.TicketNote.length > 0 ? (
                  <div className="space-y-2 max-h-[300px] overflow-y-auto">
                    {ticketDetail.TicketNote.map((note: any) => (
                      <div key={note.id} className="p-3 border rounded-lg border-l-4 border-l-primary">
                        <div className="flex items-center justify-between mb-1">
                          <span className="text-sm font-medium">
                            {note.Author
                              ? [note.Author.firstName, note.Author.lastName].filter(Boolean).join(" ") || note.Author.email
                              : "Système"}
                          </span>
                          <span className="text-xs text-muted-foreground">{formatDate(note.createdAt)}</span>
                        </div>
                        <p className="text-sm whitespace-pre-wrap">{note.message}</p>
                      </div>
                    ))}
                  </div>
                ) : (
                  <p className="text-sm text-muted-foreground text-center py-4">Aucune note</p>
                )}
              </div>
            </div>
          ) : null}
        </DialogContent>
      </Dialog>

      {/* Create Ticket Dialog */}
      <Dialog open={showCreateDialog} onOpenChange={setShowCreateDialog}>
        <DialogContent className="max-w-lg">
          <DialogHeader>
            <DialogTitle>Nouveau ticket support</DialogTitle>
            <DialogDescription>Créer un ticket pour une entreprise cliente</DialogDescription>
          </DialogHeader>

          <div className="space-y-4 mt-4">
            <div>
              <label htmlFor="create-company" className="text-sm font-medium mb-1 block">Entreprise *</label>
              {companiesError ? (
                <div className="flex items-center gap-2 text-sm text-destructive py-2">
                  <WifiOff className="h-4 w-4 flex-shrink-0" aria-hidden="true" />
                  Impossible de charger la liste des entreprises
                </div>
              ) : (
                <Select value={createForm.companyId || "_none"} onValueChange={(v) => setCreateForm(f => ({ ...f, companyId: v === "_none" ? "" : v }))}>
                  <SelectTrigger id="create-company">
                    <SelectValue placeholder="Sélectionner..." />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="_none">Sélectionner...</SelectItem>
                    {companiesData?.items.map((c: any) => (
                      <SelectItem key={c.id} value={c.id}>{c.name}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              )}
            </div>

            <div>
              <label htmlFor="create-subject" className="text-sm font-medium mb-1 block">Sujet *</label>
              <Input
                id="create-subject"
                value={createForm.subject}
                onChange={(e) => setCreateForm(f => ({ ...f, subject: e.target.value }))}
                placeholder="Résumé du problème..."
              />
            </div>

            <div className="grid grid-cols-2 gap-3">
              <div>
                <label htmlFor="create-type" className="text-sm font-medium mb-1 block">Type</label>
                <Select value={createForm.type} onValueChange={(v) => setCreateForm(f => ({ ...f, type: v }))}>
                  <SelectTrigger id="create-type">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    {Object.entries(TYPE_LABELS).map(([key, label]) => (
                      <SelectItem key={key} value={key}>{label}</SelectItem>
                    ))}
                  </SelectContent>
                </Select>
              </div>
              <div>
                <label htmlFor="create-priority" className="text-sm font-medium mb-1 block">Priorité</label>
                <Select value={createForm.priority} onValueChange={(v) => setCreateForm(f => ({ ...f, priority: v }))}>
                  <SelectTrigger id="create-priority">
                    <SelectValue />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="LOW">Basse</SelectItem>
                    <SelectItem value="MEDIUM">Moyenne</SelectItem>
                    <SelectItem value="HIGH">Haute</SelectItem>
                    <SelectItem value="URGENT">Urgent</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            </div>

            <div>
              <label htmlFor="create-description" className="text-sm font-medium mb-1 block">Description</label>
              <textarea
                id="create-description"
                className="w-full min-h-[100px] px-3 py-2 border rounded-md text-sm"
                placeholder="Détails du problème..."
                value={createForm.description}
                onChange={(e) => setCreateForm(f => ({ ...f, description: e.target.value }))}
              />
            </div>

            <div className="flex justify-end gap-2">
              <Button variant="outline" onClick={() => setShowCreateDialog(false)}>Annuler</Button>
              <Button onClick={handleCreate}
                disabled={!createForm.companyId || !createForm.subject.trim() || createMutation.isPending}>
                Créer le ticket
              </Button>
            </div>
          </div>
        </DialogContent>
      </Dialog>
    </AdminLayout>
  );
}
